import { inject, Injectable } from '@angular/core';
import { AbstractNavigationDataService, NavigationData, OverlayService } from '@finra/rampdi-forms-common';
import { environment } from '../../environments/environment';
import { ActivatedRouteSnapshot } from '@angular/router';
import { InstanceService } from './instance.service';
import { DisplayRulesService, FILING_STATUS } from './display-rules.service';

export const FORM_TYPES = {
  INITIAL: 'INITIAL',
};

// Version 0.0.1 navigation with single disclosure page
const navView_v001: NavigationData[] = [
  {
    type: 'header',
    id: 'dataEntry',
    text: environment.formName,
    skip: true,
    children: [
      {
        id: 'identifyingInformation',
        text: 'Identifying Information',
        modelPath: [],
        pointerList: [/^#\/identifyingInformation/],
      },
      {
        id: 'webAddresses',
        text: 'Web Addresses',
        modelPath: [],
        pointerList: [/^#\/webAddresses/],
      },
      {
        id: 'operationInformation',
        text: 'Operation Information',
        modelPath: [],
        pointerList: [/^#\/operationInformation/],
      },
      {
        id: 'expenseInformation',
        text: 'Expense Information',
        modelPath: [],
        pointerList: [/^#\/expenseInformation/],
      },
    ],
  },
];

const formView_v001: NavigationData[] = [
  {
    id: 'formView',
    type: 'header',
    text: 'Form View',
    skip: true,
    children: [
      {
        id: 'formViewIdentifyingInformation',
        text: 'Identifying Information',
        modelPath: [],
        pointerList: [/^#\/identifyingInformation/],
      },
      {
        id: 'formViewWebAddresses',
        text: 'Web Addresses',
        modelPath: [],
        pointerList: [/^#\/webAddresses/],
      },
      {
        id: 'formViewOperationInformation',
        text: 'Operation Information',
        modelPath: [],
        pointerList: [/^#\/operationInformation/],
      },
      {
        id: 'formViewExpenseInformation',
        text: 'Expense Information',
        modelPath: [],
        pointerList: [/^#\/expenseInformation/],
      },
    ],
  },
];

const navView: NavigationData[] = [
  {
    type: 'header',
    id: 'dataEntry',
    text: environment.formName,
    skip: true,
    children: [
      {
        id: 'identifyingInformation',
        text: 'Identifying Information',
        modelPath: [],
        pointerList: [/^#\/identifyingInformation/],
      },
      {
        id: 'webAddresses',
        text: 'Web Addresses',
        modelPath: [],
        pointerList: [/^#\/webAddresses/],
      },
      {
        id: 'operationInformation',
        text: 'Operation Information',
        modelPath: [],
        pointerList: [/^#\/operationInformation/],
      },
      {
        id: 'expenseInformation',
        text: 'Expense Information',
        modelPath: [],
        pointerList: [/^#\/expenseInformation/],
      },
    ],
  },
];

const formView: NavigationData[] = [
  {
    id: 'formView',
    type: 'header',
    text: 'Form View',
    skip: true,
    children: [
      {
        id: 'formViewIdentifyingInformation',
        text: 'Identifying Information',
        modelPath: [],
        pointerList: [/^#\/identifyingInformation/],
      },
      {
        id: 'formViewWebAddresses',
        text: 'Web Addresses',
        modelPath: [],
        pointerList: [/^#\/webAddresses/],
      },
      {
        id: 'formViewOperationInformation',
        text: 'Operation Information',
        modelPath: [],
        pointerList: [/^#\/operationInformation/],
      },
      {
        id: 'formViewExpenseInformation',
        text: 'Expense Information',
        modelPath: [],
        pointerList: [/^#\/expenseInformation/],
      },
    ],
  },
];

@Injectable({
  providedIn: 'root',
})
export class NavigationDataService extends AbstractNavigationDataService {
  licensesList: any[] = [];

  constructor(
    public OS: OverlayService,
    private DS: DisplayRulesService,
    private IS: InstanceService,
  ) {
    super({
      dataEntry: {
        [FORM_TYPES.INITIAL]: navView,
      },
      formView: {
        [FORM_TYPES.INITIAL]: formView,
      },
    });
  }

  // Override getNavData to handle version-specific navigation
  getNavData(key: string): any[] {
    const templateVersion = this.IS.currentInstance?.templateVersion;

    if (templateVersion === '0.0.1') {
      const [viewType] = key.split('.');
      return viewType === 'formView' ? [...formView_v001] : [...navView_v001];
    }

    // Normalize filingTypeCode to INITIAL — this app only has one form type
    const [viewType] = key.split('.');
    const normalizedKey = `${viewType}.${FORM_TYPES.INITIAL}`;
    return super.getNavData(normalizedKey);
  }

  // Override getNavItem to handle version-specific navigation
  getNavItem(key: string, navId: string): any {
    const templateVersion = this.IS.currentInstance?.templateVersion;

    if (templateVersion === '0.0.1') {
      const [viewType] = key.split('.');
      const navData = viewType === 'formView' ? formView_v001 : navView_v001;

      // Search for navItem in v001 navigation
      for (const nav of navData) {
        if (nav.id === navId) return nav;
        if (nav.children) {
          const found = nav.children.find((child: any) => child.id === navId);
          if (found) return found;
        }
      }
      return null;
    }

    // Normalize filingTypeCode to INITIAL — this app only has one form type
    // so any filingTypeCode (e.g., MU3) from instance metadata maps to INITIAL
    const [viewType] = key.split('.');
    const normalizedKey = `${viewType}.${FORM_TYPES.INITIAL}`;
    return super.getNavItem(normalizedKey, navId);
  }

  resolve(route: ActivatedRouteSnapshot) {
    const { instance } = route.parent.data.instanceData;
    const filingType = instance?.instanceMetadata?.attributes?.filingTypeCode;
    const templateVersion = instance?.templateVersion;

    // Use version-specific navigation for 0.0.1 (single disclosure page)
    let navData;
    if (templateVersion === '0.0.1') {
      const viewType = route.params.viewType;
      navData = viewType === 'formView' ? [...formView_v001] : [...navView_v001];
    } else {
      navData = this.getNavData(`${route.params.viewType}.${filingType}`);
    }

    // Hide "Attest and Submit" menu item for processed filings
    const currentRules = this.DS.getCurrentDisplayRules();
    const instanceStatus = currentRules?.displayedStatus;
    if (instanceStatus === 'Filing Processed' || instanceStatus === 'Failed Processing') {
      navData.forEach((nav) => {
        if (nav.children) {
          nav.children = nav.children.filter((child) => child.id !== 'attestAndSubmit');
        }
      });
    }

    if (route.params.sectionId == 'licenses' || route.params.sectionId == 'formViewLicenses') {
      this.updateChecklistNavItems(navData, instance?.collectedData?.licenses);
    }
    if (
      (route.params.sectionId == 'licenses' || route.params.sectionId == 'formViewLicenses') &&
      route?.queryParamMap?.get('bannerType')
    ) {
      this.displayBanner(route);
    } else {
      this.closeSnackbar();
    }

    const navId = route.params.sectionId + (route.params.licenseTypeCode ? `;${route.params.licenseTypeCode}` : '');

    this.removeActiveAll(navData);
    this.setActiveStatus(navData, navId, true);

    return navData;
  }

  closeSnackbar() {
    // Close any active snackbar directly instead of creating a new empty one
    if (this.OS.activeToastInstance) {
      this.OS.activeToastInstance.close();
    }
  }

  updateChecklistNavItems(navData: NavigationData[], licenses) {
    const subjectId = this.IS.currentInstance.instanceMetadata.target?.['subjectId'];
    // License service not available in MU3 — skip checklist nav updates
    let guidanceNavData: NavigationData = this.find(navData, 'guidance');
    this.licensesList = [];
    const unSubmittedApplications = this.getUnSubmittedApplications(licenses);
    const submittedApplications = this.getSubmittedApplications(licenses);
    const guidanceList = unSubmittedApplications.concat(submittedApplications);
    const uniqueGuidanceList = Array.from(
      new Map(guidanceList.reverse().map((item) => [`${item.id}_${item.text}`, item])).values(),
    ).reverse();
    const guidanceData = uniqueGuidanceList;
    if (guidanceData.length && navData[0]?.id !== 'formView') {
      if (guidanceNavData) {
        guidanceNavData.skip = true;
        ``;
        guidanceNavData.text = `Agency Guidance - ${guidanceData.length}`;
        guidanceNavData.children = guidanceData;
      } else {
        navData[0].children.splice(1, 0, {
          id: 'guidance',
          text: `Agency Guidance - ${guidanceData.length}`,
          skip: true,
          children: guidanceData,
        });
      }
    } else {
      navData[0].children = navData[0].children.filter((val) => val.id !== 'guidance');
    }
  }

  displayBanner(route) {
    switch (this.IS.currentInstance.instanceMetadata.attributes?.filingStatus?.current) {
      case FILING_STATUS.ATTESTATION_REQUESTED:
      case FILING_STATUS.ATTESTED:
        this.OS.showSnackbar({
          theme: 'warning',
          text: 'The attestation request for this filing must be recalled before additional actions can be taken.',
          autoCloseTimeMs: 1800000,
        });
        break;
      case FILING_STATUS.SUBMITTED:
      case FILING_STATUS.PROCESSED:
        let text = this.DS.userPermissions.isCompanyUser
          ? 'The previous filing submission for this individual is still being processed. Try again later.'
          : 'Your previous filing submission is still being processed. Try again later.';
        this.OS.showSnackbar({
          theme: 'warning',
          text: text,
          autoCloseTimeMs: 1800000,
        });
        break;
      default:
        let bannerType = route?.queryParamMap?.get('bannerType');
        switch (bannerType) {
          case 'SURRENDERABLE':
            this.OS.showSnackbar({
              theme: 'warning',
              text: 'You are now on the Manage Licenses section of the MU3. To complete your request, select Surrender for the applicable license(s) below and submit this filing.',
              autoCloseTimeMs: 1800000,
            });
            break;
          case 'WITHDRAWABLE':
            this.OS.showSnackbar({
              theme: 'warning',
              text: 'You are now on the Manage Licenses section of the MU3. To complete your request, select Withdraw for the applicable license(s) below and submit this filing.',
              autoCloseTimeMs: 1800000,
            });
            break;
          case 'CANCELABLE':
            this.OS.showSnackbar({
              theme: 'warning',
              text: 'You are now on the Manage Licenses section of the MU3. To complete your request, select Cancel on the applicable license(s) below and submit this filing.',
              autoCloseTimeMs: 1800000,
            });
            break;
          default:
            console.log('No banner');
            break;
        }
        break;
    }
  }

  /**
   * Determines the checklist type and display name based on license properties
   *
   * @param license - The license object to evaluate
   * @returns An object containing type and displayName for the checklist
   */
  getCheckListType(license) {
    // First check requested actions which take precedence
    if (license?.requestedAction) {
      switch (license?.requestedAction) {
        case 'SURRENDER_REQUESTED':
          return {
            type: 'Surrender',
            displayName: 'Surrender',
          };
        case 'CANCEL_REQUESTED':
          return {
            type: 'New Application',
            displayName: 'Transition',
          };
        case 'WITHDRAWAL_REQUESTED':
          return {
            type: 'New Application',
            displayName: 'New Application',
          };
      }
    }

    // Then check license status category
    if (license?.licenseStatusCategoryCode) {
      if (license?.licenseStatusCategoryCode === 'APPROVED') {
        // Special case for approved surrenders
        if (license?.licenseStatusCode === 'APPROVEDSURRENDER') {
          return {
            type: 'Surrender',
            displayName: 'Surrender',
          };
        } else {
          // All other approved licenses are amendments
          return {
            type: 'Amendment',
            displayName: 'Amendment',
          };
        }
      }

      // Pending licenses are treated as new applications
      if (license?.licenseStatusCategoryCode === 'PENDING') {
        return {
          type: 'New Application',
          displayName: 'New Application',
        };
      }
    }

    // Handle transition cases
    if (license?.transitionNumber) {
      return {
        type: 'New Application',
        displayName: 'Transition',
      };
    } else {
      // Default case - new application
      return {
        type: 'New Application',
        displayName: 'New Application',
      };
    }
  }

  getUnSubmittedApplications(licenses) {
    const applications = [...(licenses ?? [])]
      .filter(
        (l) =>
          (l?.licenseStatusDescription && l?.requestedAction?.includes('_REQUESTED')) || !l?.licenseStatusDescription,
      )
      .map(
        (license) =>
          new NavigationData(
            `guidance;${license.licenseTypeCode}`,
            `${license.regulatorShortName}: ${license.licenseTypeDescription}`,
            undefined,
            undefined,
            [],
            [],
            this.getCheckListType(license),
          ),
      );
    return applications;
  }

  getSubmittedApplications(licenses) {
    const applications = [...(licenses ?? [])]
      .filter((l) => l?.licenseStatusDescription)
      .map(
        (license) =>
          new NavigationData(
            `guidance;${license.licenseTypeCode}`,
            `${license.regulatorShortName}: ${license.licenseTypeDescription}`,
            undefined,
            undefined,
            [],
            [],
            this.getCheckListType(license),
          ),
      );
    return applications;
  }
}
